import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Segmented, Tooltip, notification } from 'antd';
import { CheckCircleFilled, MinusCircleOutlined } from '@ant-design/icons';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { paths } from 'routes/routes';
import { colors } from 'theme/tokens';
import { Pill } from 'pages/support-shared/components';
import { MIN_REASON, fmtDate, fmtDateTime, fmtNumber } from 'pages/access-groups/common';
import {
  ActionConfig,
  ActionModal,
  EmptyHint,
  NameList,
  PersonCell,
  TIER_LABEL,
  departmentText,
  personName,
  readBulk,
  showBulkResult,
  useStaff,
  useStaffList,
} from './shared';
import DataView, { DataColumn } from './data-view';
import TabHead from './tab-head';
import {
  ConfirmBody,
  RevokeBody,
  StaffAccessSummary,
  StaffReviewRow,
  StaffReviewTab,
} from './types';
import styles from './styles.module.scss';

const PAGE_SIZE = 20;
const BULK_MAX = 50;

interface Props {
  /** LEGACY = thẻ "Cần rà soát" (có công tắc Chưa / Đã xác nhận) · OPS = thẻ "Quản lý vận hành". */
  kind: 'LEGACY' | 'OPS';
}

/** Công tắc trong thẻ "Cần rà soát": LEGACY = chưa xác nhận · CONFIRMED = đã xác nhận. */
type ReviewView = 'LEGACY' | 'CONFIRMED';

const YesNo = ({ yes, yesText, noText }: { yes: boolean; yesText: string; noText: string }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    {yes ? (
      <CheckCircleFilled style={{ color: colors.successInk }} aria-hidden />
    ) : (
      <MinusCircleOutlined style={{ color: colors.textSecondary }} aria-hidden />
    )}
    {yes ? yesText : noText}
  </span>
);

/**
 * Thẻ "Cần rà soát" (người đang là nhân viên do tự khai trước đây, chưa được Admin xác nhận) và
 * thẻ "Quản lý vận hành" (MOD thuộc phòng "QL Vận Hành"). Cùng kiểu dòng StaffReviewRow.
 */
const ReviewTab = ({ kind }: Props) => {
  const ops = kind === 'OPS';
  const confirmLabel = ops ? 'Xác nhận đúng người' : 'Xác nhận là nhân viên';
  const roleText = ops ? 'là Quản lý vận hành' : 'là nhân viên';
  const { refreshKey, afterAction, summary } = useStaff();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<StaffReviewRow[]>([]);
  const [action, setAction] = useState<ActionConfig | null>(null);
  const [view, setView] = useState<ReviewView>('LEGACY');
  const tab: StaffReviewTab = ops ? 'OPS' : view;
  // "Đã xác nhận": chỉ xem và gỡ từng người, không có việc hàng loạt.
  const confirmedView = tab === 'CONFIRMED';

  const { items, total, loading, error, reload } = useStaffList<StaffReviewRow>(
    API.staffAccessReview,
    { tab, page, limit: PAGE_SIZE, q },
    refreshKey
  );

  useEffect(() => setSelected([]), [refreshKey, q, tab]);

  // Xử lý hết các dòng của trang cuối: lùi về trang cuối còn dữ liệu, không để bảng trống.
  useEffect(() => {
    if (!loading && !error && items.length === 0 && total > 0 && page > 1) {
      setPage(Math.max(1, Math.ceil(total / PAGE_SIZE)));
    }
  }, [loading, error, items.length, total, page]);

  const nameById = useMemo(() => {
    const map = new Map<number, string>();
    [...items, ...selected].forEach((row) => map.set(row.user.id, personName(row.user)));
    return map;
  }, [items, selected]);
  const nameOf = (id: number) => nameById.get(id) || `Tài khoản số ${id}`;

  const confirm = (rows: StaffReviewRow[]) => {
    const one = rows.length === 1 ? rows[0] : null;
    setAction({
      title: one
        ? `Xác nhận ${personName(one.user)} ${roleText}`
        : `Xác nhận ${rows.length} người ${roleText}`,
      okText: one ? confirmLabel : `Xác nhận ${rows.length} người`,
      description: (
        <>
          <p style={{ marginBottom: 8 }}>
            {ops
              ? 'Bạn xác nhận những người dưới đây đúng là Quản lý vận hành của công ty. Phòng ban và quyền của họ giữ nguyên.'
              : 'Bạn xác nhận những người dưới đây đúng là nhân viên của công ty. Phòng ban và ca làm của họ giữ nguyên, họ sẽ rời khỏi danh sách cần rà soát, vào được Bảng tin nội bộ và thấy Điểm Sao trên app.'}
          </p>
          <NameList names={rows.map((row) => personName(row.user))} />
        </>
      ),
      noteLabel: 'Ghi chú (không bắt buộc)',
      noteMin: 0,
      onSubmit: async (note) => {
        const body: ConfirmBody = { userIds: rows.map((row) => row.user.id) };
        if (note) body.note = note;
        const res = await AxiosUtils.post(API.staffAccessConfirm, body);
        const result = readBulk(res);
        if (one && !result.failed.length) {
          notification.success({ message: `Đã xác nhận ${personName(one.user)}.` });
        } else {
          showBulkResult(result, 'Đã xác nhận', nameOf);
        }
        afterAction();
      },
    });
  };

  const revoke = (row: StaffReviewRow) => {
    const department = departmentText(row.user.department, row.user.departmentLabel);
    // Bảng tin / ví Sao chỉ đổi với tài khoản vai trò User (Creator Studio giữ lớp Creator).
    const plainUser = String(row.user.roles).trim() === 'USER';
    setAction({
      title: `Gỡ ${personName(row.user)} khỏi nhân viên`,
      okText: 'Gỡ khỏi nhân viên',
      danger: true,
      description: (
        <>
          <div className={`${styles.effect} ${styles.effectRemove}`}>
            <strong>Sẽ bị gỡ:</strong>
            <ul>
              <li>Phòng ban{department ? ` (${department})` : ''}.</li>
              <li>Đánh dấu “thuộc công ty HP”.</li>
              <li>Ca làm mặc định.</li>
              <li>
                Các ca đã xếp cho những ngày sắp tới, và ca hôm nay nếu họ chưa chấm công (gỡ xong
                họ không chấm công ca đó được nữa).
              </li>
              {row.confirmation && <li>Xác nhận nhân viên mà Admin đã làm trước đây.</li>}
              {plainUser && (
                <li>
                  Quyền vào Bảng tin nội bộ (trừ khi được cấp riêng ở Bảng tin → Thành viên).
                </li>
              )}
              {plainUser && (
                <li>
                  Nhóm Nhân viên của ví Sao: ví chuyển về “Không tham gia”, hết nhận Sao tự động
                  (chuỗi tuần, ca chuẩn, thâm niên…).
                </li>
              )}
            </ul>
          </div>
          <div className={`${styles.effect} ${styles.effectKeep}`}>
            <strong>Được GIỮ nguyên:</strong>
            <ul>
              <li>Lịch sử chấm công và lương (bảng công, phiếu lương đã có).</li>
              {plainUser && <li>Số Sao đang có trong ví.</li>}
              <li>Tài khoản: không bị khoá, vẫn đăng nhập app như người dùng thường.</li>
            </ul>
          </div>
        </>
      ),
      noteLabel: `Lý do gỡ (bắt buộc, ít nhất ${MIN_REASON} ký tự)`,
      noteMin: MIN_REASON,
      notePlaceholder: 'Ví dụ: Không có trong danh sách nhân sự, tự khai lúc đăng ký.',
      onSubmit: async (note) => {
        const body: RevokeBody = { userId: row.user.id, note };
        await AxiosUtils.post(API.staffAccessRevoke, body);
        notification.success({ message: `Đã gỡ ${personName(row.user)} khỏi nhân viên.` });
        afterAction();
      },
    });
  };

  const person: DataColumn<StaffReviewRow> = {
    key: 'user',
    title: 'Người',
    width: 260,
    slot: 'head',
    render: (row) => (
      <PersonCell user={row.user} extra={`Đăng ký ${fmtDate(row.user.createdAt)}`} />
    ),
  };

  // Hai tín hiệu chấm công gộp một cột: số lần trong 60 ngày + lần gần nhất.
  const attendance: DataColumn<StaffReviewRow> = {
    key: 'attendance',
    title: 'Chấm công 60 ngày',
    width: 210,
    render: (row) => (
      <span>
        <span className={styles.number}>{fmtNumber(row.signals?.attendanceCount60d ?? 0)}</span> lần
        <span className={styles.sub} style={{ display: 'block' }}>
          {row.signals?.lastAttendanceAt
            ? `Gần nhất ${fmtDateTime(row.signals.lastAttendanceAt)}`
            : 'Chưa chấm công lần nào'}
        </span>
      </span>
    ),
  };

  const payslip: DataColumn<StaffReviewRow> = {
    key: 'payslip',
    title: 'Phiếu lương đã chốt',
    width: 170,
    render: (row) => (
      <YesNo yes={!!row.signals?.hasFinalPayslip} yesText="Đã có" noText="Chưa có" />
    ),
  };

  const actions: DataColumn<StaffReviewRow> = {
    key: 'actions',
    title: 'Thao tác',
    width: 200,
    slot: 'actions',
    render: (row) => (
      <span className={styles.actionsStack}>
        {!confirmedView && !row.confirmation && (
          <Button size="small" type="primary" onClick={() => confirm([row])}>
            {confirmLabel}
          </Button>
        )}
        {/* Admin / Quản lý (có cấp duyệt) không gỡ ở đây (máy chủ trả STAFF_REVOKE_PRIVILEGED). */}
        {ops || row.tier ? (
          <Tooltip
            title={
              ops
                ? 'Mở trang Tài khoản để đổi phòng ban của người này.'
                : 'Admin và Quản lý không gỡ ở đây. Mở trang Tài khoản để đổi phòng ban của người này.'
            }
          >
            <Link to={paths.accountManagement}>
              <Button size="small" block>
                Đổi phòng ban
              </Button>
            </Link>
          </Tooltip>
        ) : (
          <Button size="small" danger onClick={() => revoke(row)}>
            Gỡ khỏi nhân viên
          </Button>
        )}
      </span>
    ),
  };

  const confirmationColumn: DataColumn<StaffReviewRow> = {
    key: 'confirmation',
    title: 'Admin xác nhận',
    width: 180,
    render: (row) =>
      row.confirmation ? (
        <span>
          <YesNo yes yesText="Đã xác nhận" noText="" />
          <span className={styles.sub} style={{ display: 'block' }}>
            {[row.confirmation.confirmedBy?.name, fmtDateTime(row.confirmation.confirmedAt)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
      ) : (
        <YesNo yes={false} yesText="" noText="Chưa xác nhận" />
      ),
  };

  const departmentColumn: DataColumn<StaffReviewRow> = {
    key: 'department',
    title: 'Phòng ban',
    width: 140,
    render: (row) =>
      departmentText(row.user.department, row.user.departmentLabel) || (
        <span className={styles.sub}>Chưa có</span>
      ),
  };

  // Tổng bề rộng (kể cả cột tích 48px): Cần rà soát 1.028px, Quản lý vận hành 1.038px, Đã xác
  // nhận 1.000px (không có cột tích) — vừa vùng nội dung 1.046px của màn 1366×768.
  let columns: DataColumn<StaffReviewRow>[];
  if (ops) {
    columns = [
      { ...person, width: 250 },
      {
        key: 'tier',
        title: 'Cấp duyệt',
        width: 170,
        render: (row) =>
          row.tier ? (
            <Pill color={colors.purpleInk} bg={colors.purpleSoft}>
              {TIER_LABEL[row.tier] || row.tier}
            </Pill>
          ) : (
            <span className={styles.sub}>Chưa có cấp duyệt</span>
          ),
      },
      { ...attendance, width: 190 },
      confirmationColumn,
      actions,
    ];
  } else if (confirmedView) {
    columns = [
      person,
      departmentColumn,
      { ...confirmationColumn, width: 190 },
      attendance,
      actions,
    ];
  } else {
    columns = [person, departmentColumn, attendance, payslip, actions];
  }

  const counts: StaffAccessSummary['counts'] = summary.counts || {
    pending: 0,
    needsReview: 0,
    opsManagers: 0,
  };
  // Số trên công tắc; máy chủ cũ chưa gửi `confirmed` thì chỉ hiện chữ.
  const withCount = (text: string, count?: number) =>
    typeof count === 'number' ? `${text} (${fmtNumber(count)})` : text;

  let lead =
    'Những người dưới đây đang là nhân viên vì đã tự khai trước đây, chưa được Admin xác nhận. Họ vẫn giữ quyền chấm công, ca làm cho tới khi bạn quyết định, nhưng chưa vào được Bảng tin nội bộ và chưa thấy Điểm Sao trên app. Số lần chấm công và phiếu lương giúp bạn nhận ra ai đang đi làm thật.';
  if (ops) {
    lead =
      'Phòng “QL Vận Hành” quyết định cấp duyệt của tài khoản MOD: MOD thuộc phòng này là Quản lý vận hành, MOD ở phòng khác là Quản lý tổng (SMOD). Hãy chắc rằng chỉ đúng người nằm trong danh sách này.';
  } else if (confirmedView) {
    lead =
      'Những người dưới đây đã được Admin xác nhận là nhân viên (duyệt yêu cầu hoặc xác nhận khi rà soát). Ai đã nghỉ việc hoặc xác nhận nhầm thì bấm “Gỡ khỏi nhân viên”.';
  }

  let empty = (
    <EmptyHint
      title="Không còn ai cần rà soát"
      text="Mọi nhân viên hiện có đều đã được Admin xác nhận."
    />
  );
  if (q) {
    empty = <EmptyHint title="Không thấy ai khớp ô tìm" text="Thử tìm bằng email hoặc bỏ bớt chữ." />;
  } else if (ops) {
    empty = (
      <EmptyHint
        title="Chưa có ai thuộc phòng “QL Vận Hành”"
        text="Muốn một tài khoản MOD thành Quản lý vận hành, đổi phòng ban của họ ở trang Tài khoản."
      />
    );
  } else if (confirmedView) {
    empty = (
      <EmptyHint
        title="Chưa có ai được xác nhận"
        text="Khi bạn duyệt yêu cầu ở thẻ Chờ duyệt hoặc xác nhận người ở mục Chưa xác nhận, họ sẽ hiện ở đây."
      />
    );
  }

  return (
    <div>
      <TabHead />
      {/* Máy chủ cũ chưa có danh sách "Đã xác nhận" (không gửi counts.confirmed): ẩn công tắc. */}
      {!ops && typeof counts.confirmed === 'number' && (
        <Segmented<ReviewView>
          className={styles.viewSwitch}
          aria-label="Chọn danh sách: chưa xác nhận hoặc đã xác nhận"
          value={view}
          onChange={(next) => {
            setView(next);
            setPage(1);
          }}
          options={[
            { value: 'LEGACY', label: withCount('Chưa xác nhận', counts.needsReview) },
            { value: 'CONFIRMED', label: withCount('Đã xác nhận', counts.confirmed) },
          ]}
        />
      )}
      <p className={styles.lead}>{lead}</p>
      <div className={styles.toolbar}>
        <Input.Search
          allowClear
          className={styles.search}
          placeholder="Tìm theo tên, email hoặc TikTok ID"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onSearch={(value) => {
            setPage(1);
            setQ(value.trim());
          }}
        />
        {!confirmedView && (
          <div className={styles.bulk} aria-live="polite">
            {selected.length > 0 ? (
              <>
                <span>
                  Đã chọn {selected.length}/{BULK_MAX}
                </span>
                <Button type="primary" onClick={() => confirm(selected)}>
                  Xác nhận {selected.length} người
                </Button>
                <Button type="text" onClick={() => setSelected([])}>
                  Bỏ chọn
                </Button>
              </>
            ) : (
              <span>Tích ô đầu dòng để xác nhận nhiều người một lần (tối đa {BULK_MAX}).</span>
            )}
          </div>
        )}
      </div>
      <DataView<StaffReviewRow>
        columns={columns}
        items={items}
        rowKey={(row) => row.user.id}
        loading={loading}
        error={error}
        onRetry={reload}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPage={setPage}
        selection={{
          selected,
          max: BULK_MAX,
          onChange: setSelected,
          disabled: (row) => !!row.confirmation,
          label: (row) => personName(row.user),
        }}
        noSelection={confirmedView}
        empty={empty}
      />
      <ActionModal config={action} onClose={() => setAction(null)} />
    </div>
  );
};

export default ReviewTab;
